import Box from '@mui/material/Box';
import Paper from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from 'components/Button/Button';
import IconButton from 'components/IconButton/IconButton';
import Checkbox from 'components/form/Checkbox';
import Label from 'components/form/FormLabel';
import Switch from 'components/form/Switch';
import { dateToMinutes } from 'components/form/TimePicker';
import { TimePickerBase } from 'components/form/TimePicker';
import { AddTimeIcon, CopyIcon, DeleteIcon } from 'components/icons';
import extend from 'lodash/extend';
import set from 'lodash/set';
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import * as React from 'react';

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const WEEKDAYS_LONG = {
  SUN: 'Sunday',
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
  SAT: 'Saturday',
};

export function timeStrToMinutes(val) {
  const [hours, minutes] = val.split(':');
  const m = moment
    .utc()
    .startOf('day')
    .add(hours, 'hours')
    .add(minutes, 'minutes');
  return dateToMinutes(m);
}

export const fromDbValue = (value) => {
  if (typeof value === 'string' && value) {
    const [from, to] = value.split('-');
    return {
      from: timeStrToMinutes(from),
      to: timeStrToMinutes(to),
    };
  } else {
    return value;
  }
};

export const timeValidator = (rule, value, callback) => {
  if (value) {
    const errors = {};
    Object.keys(value).forEach((day) => {
      const slots = value[day];
      if (!slots || !slots.length) {
        return;
      }
      slots.forEach((slot, index) => {
        if (slot.from >= slot.to) {
          set(
            errors,
            [day, index, 'from'],
            `From time cannot be greater than or same as To time`,
          );
        } else if (slot.to <= slot.from) {
          set(
            errors,
            [day, index, 'to'],
            `To time cannot be less than or same as From time`,
          );
        }
        if (index > 0) {
          const prevSlot = slots[index - 1];
          if (slot.from < prevSlot.to) {
            set(errors, [day, index], `Time slot overlaps with previous slot`);
          }
        }
      });
    });
    if (Object.keys(errors).length) {
      callback(errors);
    } else {
      callback();
    }
  } else {
    callback();
  }
};

function momentToMinutes(m) {
  if (moment.isMoment(m)) {
    return dateToMinutes(m);
  }
  return m;
}

function doesNotOverlapWithUpdate(updatedSlot, existingSlots, index) {
  const existingSlot = existingSlots[index];
  for (const slot of existingSlots) {
    if (existingSlot === slot) continue;
    if (updatedSlot.from >= slot.to || updatedSlot.to <= slot.from) {
      continue; // No overlap, check the next existing slot.
    } else {
      return false; // 'from' value overlaps with an existing slot.
    }
  }
  return true; // 'from' value does not overlap with any existing slots.
}

const TimeSlot = ({
  index,
  slot,
  error,
  onChangeFrom,
  onChangeTo,
  onRemove,
}) => {
  const errStr = typeof error === 'string' ? error : null;

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center">
        <TimePickerBase
          error={error?.from}
          value={slot.from}
          onChange={onChangeFrom(index)}
          maxTime={slot.to}
        />
        <Typography variant="caption" fontWeight={500} color="text.secondary">
          TO
        </Typography>
        <TimePickerBase
          error={error?.to}
          minTime={slot.from}
          value={slot.to}
          onChange={onChangeTo(index)}
        />
        <Box>
          <IconButton
            edge="end"
            color="error"
            onClick={() => onRemove(index)}
            size="mini"
            variant="text"
            icon={<DeleteIcon />}
          />
        </Box>
      </Stack>
      {errStr && <FormHelperText sx={{ m: 0 }}>{errStr}</FormHelperText>}
    </>
  );
};

const WeekdaySchedule = ({
  value,
  onChange,
  weekday,
  allowMulti = true,
  hasError,
  error,
}) => {
  const state = usePopupState({ popupId: weekday, variant: 'popover' });
  const [selectedDays, setSelectedDays] = React.useState([]);

  const onChangeFrom = (idx) => (time) => {
    const val = value[idx];
    const parsed = momentToMinutes(time);
    extend(val, { from: parsed });
    onChange({
      [weekday]: value,
    });
  };

  const onChangeTo = (idx) => (time) => {
    const val = value[idx];
    const parsed = momentToMinutes(time);
    extend(val, { to: parsed });
    onChange({
      [weekday]: value,
    });
  };

  const onToggle = (e) => {
    const { checked } = e.target;
    if (checked) {
      onChange({
        [weekday]: [{ from: 480, to: 1020 }],
      });
    } else {
      onChange({
        [weekday]: [],
      });
    }
  };

  const onRemove = (idx) => {
    onChange({
      [weekday]: value.filter((v, i) => i !== idx),
    });
  };

  const onAdd = () => {
    if (value.length) {
      const last = value[value.length - 1];
      onChange({
        [weekday]: [
          ...value,
          {
            from: last.to,
            to: last.to + 60,
          },
        ],
      });
    } else {
      onChange({
        [weekday]: [{ from: 480, to: 1020 }],
      });
    }
  };

  const onCopy = () => {
    state.close();
    const values = {};
    selectedDays.forEach((day) => {
      values[day] = value.map((i) => ({
        from: i.from,
        to: i.to,
      }));
    });
    onChange(values);
    setSelectedDays([]);
  };

  const isEnabled = value?.length > 0;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ px: 1, pl: 2, py: 1 }}
      >
        <Typography fontWeight={600} sx={{ flex: 1 }}>
          {WEEKDAYS_LONG[weekday]}
        </Typography>
        {isEnabled && (
          <>
            {allowMulti && (
              <IconButton
                onClick={onAdd}
                size="mini"
                variant="text"
                color="secondary"
                icon={<AddTimeIcon />}
              />
            )}
            <IconButton
              {...bindTrigger(state)}
              size="mini"
              variant="text"
              color="secondary"
              icon={<CopyIcon />}
            />
          </>
        )}
        <Switch
          margin="none"
          size="small"
          checked={isEnabled}
          onChange={onToggle}
          labelPlacement="start"
        />
      </Stack>
      {value?.length === 0 ? null : (
        <>
          <Divider />
          <Stack
            spacing={1}
            justifyContent="center"
            sx={{ bgcolor: 'background.paper3', p: 2 }}
          >
            {value.map((i, idx) => (
              <TimeSlot
                key={idx}
                slot={i}
                index={idx}
                onChangeTo={onChangeTo}
                onChangeFrom={onChangeFrom}
                onRemove={onRemove}
                error={error && error[idx]}
              />
            ))}
          </Stack>
        </>
      )}
      <Popover {...bindPopover(state)}>
        <Box p={2}>
          <Typography gutterBottom color="text.secondary" variant="body2">
            Copy to..
          </Typography>
          {WEEKDAYS.map((day) => (
            <Checkbox
              disabled={weekday === day}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedDays([...selectedDays, day]);
                } else {
                  setSelectedDays(selectedDays.filter((i) => i !== day));
                }
              }}
              checked={selectedDays.includes(day)}
              key={day}
              size="small"
              margin="none"
              label={day}
            />
          ))}
          <Button fullWidth size="small" sx={{ mt: 1 }} onClick={onCopy}>
            Apply
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

const WorkingHoursSelect = React.forwardRef(
  (
    { value, onChange, hasError, helpText, label, allowMulti, onError, width },
    ref,
  ) => {
    const handleChange = (val) => {
      onChange({
        ...value,
        ...val,
      });
    };

    return (
      <FormControl fullWidth margin="dense" error={hasError} ref={ref}>
        {label && <Label>{label}</Label>}
        <Paper
          elevation={0}
          variant="containedVariant"
          sx={{ borderColor: hasError && 'error.main', minWidth: 400, width }}
        >
          <Stack divider={<Divider />}>
            {WEEKDAYS.map((day) => {
              const v = value?.[day] || [];
              const error = hasError && helpText && helpText[day];
              return (
                <WeekdaySchedule
                  key={day}
                  weekday={day}
                  onChange={handleChange}
                  value={v}
                  allowMulti={allowMulti}
                  hasError={hasError}
                  error={error}
                />
              );
            })}
          </Stack>
        </Paper>
        {/*{hasError && <FormHelperText sx={{ m: 0 }}>{helpText}</FormHelperText>}*/}
      </FormControl>
    );
  },
);

WorkingHoursSelect.propTypes = {
  label: PropTypes.string,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func,
  allowMulti: PropTypes.bool,
};

export default WorkingHoursSelect;
